import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Gift, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ReferralCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditsAwarded: number;
  referrerUsername: string;
  referrerName: string;
}

export const ReferralCreditsDialog = ({

  open,
  onOpenChange,
  creditsAwarded,
  referrerUsername,
  referrerName,
}: ReferralCreditsDialogProps) => {
  const { t } = useTranslation();
  const displayCredits = creditsAwarded / 10; // Convert to display credits

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <div className="flex flex-col items-center justify-center py-8 text-center space-y-6 animate-scale-in">
          {/* Animated Gift Icon */}
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-primary to-primary/60 p-6 rounded-full">
              <Gift className="w-12 h-12 text-primary-foreground animate-[bounce_1s_ease-in-out_3]" />
            </div>
            <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-500 animate-[spin_3s_linear_infinite]" />
            <Sparkles className="absolute -bottom-2 -left-2 w-5 h-5 text-yellow-500 animate-[spin_4s_linear_infinite_reverse]" />
          </div>

          {/* Success Message */}
          <div className="space-y-2 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">{t('ui.text.congratulations')}</h2>
            <p className="text-lg text-muted-foreground">{t('ui.text.you_ve_earned_referral_credits')}</p>
          </div>

          {/* Credits Display */}
          <div className="bg-primary/10 border-2 border-primary/30 rounded-lg p-6 w-full animate-scale-in" style={{ animationDelay: "0.4s" }}>
            <div className="text-5xl font-bold text-primary mb-2">
              +{displayCredits}
            </div>
            <div className="text-sm text-muted-foreground">
              credits added to your account
            </div>
          </div>

          {/* Referrer Info */}
          <div className="animate-fade-in" style={{ animationDelay: "0.6s" }}>
            <p className="text-sm text-muted-foreground">{t('ui.text.thanks_to')}<span className="font-semibold text-foreground">@{referrerUsername}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {referrerName} also received {displayCredits} credits!
            </p>
          </div>

          {/* Close Button */}
          <Button 
            onClick={() => onOpenChange(false)} 
            className="w-full animate-fade-in"
            style={{ animationDelay: "0.8s" }}
          >{t('ui.text.awesome')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
